/**
 * Kho ảnh món dựng sẵn.
 *
 * Rào cản lớn nhất khi một quán đổi sang phần mềm mới là nhập 50 món bằng tay. App đã bớt
 * được phần tên và giá bằng cách chụp bảng giá; kho ảnh này bớt nốt phần còn lại: chủ quán
 * bấm một tấm ảnh, gõ giá, xong một món.
 *
 * Ảnh nằm trong `public/menu/`, đi kèm bản cài nên <b>mất mạng vẫn hiện</b>. Sản phẩm chỉ
 * lưu cái mã ngắn (`ca-phe-sua-da`), không lưu ảnh — nhờ vậy gói danh mục kéo về máy vẫn
 * nhẹ như trước, dù mỗi món đều có hình.
 *
 * Món hiếm không có trong kho thì chủ quán tự tải ảnh lên; khi đó mã bắt đầu bằng `u/`.
 */

export interface MenuImage {
  /** Mã lưu vào sản phẩm. Cũng là tên file trong `public/menu/`. */
  key: string
  name: string
  group: string
  /** Đơn vị hay dùng cho món này — điền sẵn để chủ quán khỏi chọn. */
  unit: string
}

export const MENU_IMAGES: MenuImage[] = [
  { key: 'ca-phe-sua-da', name: 'Cà phê sữa đá', group: 'Cà phê', unit: 'ly' },
  { key: 'ca-phe-den-da', name: 'Cà phê đen đá', group: 'Cà phê', unit: 'ly' },
  { key: 'ca-phe-nong', name: 'Cà phê nóng', group: 'Cà phê', unit: 'ly' },

  { key: 'matcha-latte', name: 'Matcha latte', group: 'Trà & trà sữa', unit: 'ly' },
  { key: 'tra-sua-tran-chau', name: 'Trà sữa trân châu', group: 'Trà & trà sữa', unit: 'ly' },
  { key: 'tra-dao', name: 'Trà đào', group: 'Trà & trà sữa', unit: 'ly' },
  { key: 'tra-chanh', name: 'Trà chanh', group: 'Trà & trà sữa', unit: 'ly' },

  { key: 'sinh-to-xoai', name: 'Sinh tố xoài', group: 'Nước ép & sinh tố', unit: 'ly' },
  { key: 'nuoc-ep-cam', name: 'Nước ép cam', group: 'Nước ép & sinh tố', unit: 'ly' },

  { key: 'pho-bo', name: 'Phở bò', group: 'Món chính', unit: 'tô' },
  { key: 'mi-xao-bo', name: 'Mì xào bò', group: 'Món chính', unit: 'dĩa' },
  { key: 'com-chien-hai-san', name: 'Cơm chiên hải sản', group: 'Món chính', unit: 'dĩa' },
  { key: 'com-ga-xoi-mo', name: 'Cơm gà xối mỡ', group: 'Món chính', unit: 'dĩa' },

  { key: 'banh-mi-thit', name: 'Bánh mì thịt', group: 'Ăn kèm', unit: 'ổ' },
  { key: 'goi-cuon', name: 'Gỏi cuốn', group: 'Ăn kèm', unit: 'phần' },
  { key: 'khoai-tay-chien', name: 'Khoai tây chiên', group: 'Ăn kèm', unit: 'phần' },

  { key: 'banh-tiramisu', name: 'Bánh tiramisu', group: 'Tráng miệng', unit: 'cái' },
  { key: 'banh-flan', name: 'Bánh flan', group: 'Tráng miệng', unit: 'cái' },
]

export const MENU_GROUPS = [...new Set(MENU_IMAGES.map((m) => m.group))]

const byKey = new Map(MENU_IMAGES.map((m) => [m.key, m]))

export const stockImage = (key: string | null | undefined): MenuImage | undefined =>
  key ? byKey.get(key) : undefined

/**
 * Đường dẫn ảnh của một mã.
 *
 * Ảnh dựng sẵn nằm cùng bản cài nên đi đường tương đối. Ảnh chủ quán tự tải lên nằm trên
 * máy chủ API — khác tên miền với app bán hàng, nên phải ghép đúng gốc của máy chủ ấy.
 */
export function imageUrl(key: string | null | undefined): string | null {
  if (!key) return null
  if (key.startsWith('u/')) {
    const base = import.meta.env.VITE_API_URL ?? ''
    return `${base}/uploads/products/${key.slice(2)}.jpg`
  }
  return byKey.has(key) ? `/menu/${key}.jpg` : null
}

/**
 * Đoán ảnh dựng sẵn cho một cái tên món.
 *
 * Dùng khi chụp bảng giá ra một loạt món: máy tự gắn ảnh cho món nào tên khớp, chủ quán
 * chỉ phải chọn tay những món còn lại. So sau khi bỏ dấu — người ta gõ "ca phe sua da"
 * cũng phải ra đúng món ấy.
 */
export function guessImage(name: string): string | null {
  const want = bare(name)
  if (!want) return null

  const exact = MENU_IMAGES.find((m) => bare(m.name) === want)
  if (exact) return exact.key

  // Khớp lỏng: tên món chứa trọn tên trong kho, hoặc ngược lại. "Cà phê sữa đá size L"
  // vẫn ra ảnh cà phê sữa đá.
  const loose = MENU_IMAGES.find((m) => want.includes(bare(m.name)) || bare(m.name).includes(want))
  return loose?.key ?? null
}

const bare = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
